import { useContext, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { AuthLayout, BrandLogo, SignUpForm } from "@/components"
import TurnstileWidget from "../ui/TurnstileWidget"
import { TURNSTILE_SITE_KEY } from "../config/index.js"
import { api } from "../system/api.js"
import { ThemeContext } from "../providers/ThemeProvider.jsx"

export default function SignUp() {
  const navigate = useNavigate()
  const { theme } = useContext(ThemeContext)
  const isDark = theme === "dark"

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [formError, setFormError] = useState(null)
  const [turnstileToken, setTurnstileToken] = useState("")
  const [captchaReady, setCaptchaReady] = useState(false)
  const turnstileRef = useRef(null)

  const validate = ({ name, email, password, termsAccepted }) => {
    const next = {}
    if (!name.trim()) next.name = "Name is required"
    if (!email.trim()) next.email = "Email is required"
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "Enter a valid email"
    if (!password) next.password = "Password is required"
    else if (password.length < 8) next.password = "Must be at least 8 characters"
    if (!termsAccepted) next.terms = "You must accept the terms"
    return next
  }

  const handleSubmit = async (values) => {
    if (loading) return

    const next = validate(values)
    setErrors(next)
    setFormError(null)
    if (Object.keys(next).length > 0) return

    if (!turnstileToken) {
      setFormError("Please complete the security verification.")
      return
    }

    setLoading(true)
    try {
      await api("sign/up", {
        name: values.name,
        email: values.email,
        password: values.password,
        turnstileToken,
      })
      navigate("/")
    } catch (err) {
      setLoading(false)
      // Turnstile tokens are single-use — the failed attempt consumed it
      setTurnstileToken("")
      setCaptchaReady(false)
      turnstileRef.current?.reset()
      const code = err?.errorCode
      if (code === "EMAIL_ALREADY_EXISTS") {
        setErrors({
          email: (
            <>
              Email already registered.{" "}
              <Link to="/sign/in" className="underline">
                Log in
              </Link>
              ?
            </>
          ),
        })
      } else if (code === "TURNSTILE_REQUIRED") {
        setFormError("Please complete the security verification.")
      } else if (code === "TURNSTILE_INVALID") {
        setFormError("Security verification failed. Please try again.")
      } else {
        setFormError("An error occurred. Please try again.")
      }
    }
  }

  const captchaSlot = (
    <div className="flex flex-col gap-2">
      {formError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-caption text-destructive">
          {formError}
        </div>
      )}
      <TurnstileWidget
        ref={turnstileRef}
        siteKey={TURNSTILE_SITE_KEY}
        onToken={(token) => {
          setTurnstileToken(token)
          setCaptchaReady(true)
          setFormError("")
        }}
        onError={() => {
          setTurnstileToken("")
          setCaptchaReady(false)
          setFormError("Security verification failed. Please try again.")
        }}
        onExpire={() => {
          setTurnstileToken("")
          setCaptchaReady(false)
        }}
        theme={isDark ? "dark" : "light"}
        appearance="interaction-only"
      />
    </div>
  )

  return (
    <AuthLayout
      brandSlot={
        <a href="https://algobuilder.com">
          <BrandLogo height={22} theme={isDark ? "dark" : "light"} />
        </a>
      }
      footerSlot={
        <>&copy; {new Date().getFullYear()} AlgoBuilder. All rights reserved.</>
      }
    >
      <SignUpForm
        heading="Subscribe today"
        description="Start building and testing your trading strategies."
        loading={loading || !captchaReady}
        submittingLabel={loading ? "Creating account..." : "Verifying you're human..."}
        errors={errors}
        captchaSlot={captchaSlot}
        termsLabel={
          <>
            I have read and accept the{" "}
            <a
              href="https://algobuilder.com/terms-and-conditions/"
              className="text-primary hover:text-primary/80 no-underline transition-colors"
            >
              Terms &amp; Conditions
            </a>
            . See our{" "}
            <a
              href="https://algobuilder.com/privacy-policy/"
              className="text-primary hover:text-primary/80 no-underline transition-colors"
            >
              Privacy Policy
            </a>
            .
          </>
        }
        signInPrompt="Already have an account?"
        signInLinkSlot={
          <Link
            to="/sign/in"
            className="text-primary hover:text-primary/80 no-underline font-semibold transition-colors"
          >
            Login
          </Link>
        }
        onSubmit={handleSubmit}
      />
    </AuthLayout>
  )
}
